/**
 * Session-lease lifecycle helper for {@link ./worker-pool.js WorkerPool}.
 *
 * The pool coordinator still owns scheduling, spawning, hard retire, and waiter
 * draining. This helper owns the lease-specific lifecycle around a checked-out
 * child: TTL heartbeat/sweep, driver health/reset hooks on release, planned
 * recycle, and `onBreach: 'drain'` deferred retirement.
 *
 * @module worker-pool/lease-coordinator
 */

import type { BrowserLogger } from '../logger.js';
import type { LeaseState, PooledChild } from './bookkeeping.js';
import { WorkerPoolError, WorkerPoolLeaseExpiredError } from './errors.js';
import type {
  Lease,
  WorkerDriver,
  WorkerPoolLeaseGraceCounts,
  WorkerPoolLeaseGraceOutcome,
  WorkerPoolLeaseOptions,
} from './types.js';

/**
 * Default lease TTL (ms) when {@link WorkerPool.lease} is called without one --
 * the window a leaseholder has to `heartbeat()` before the pool reclaims its
 * child. A consumer must heartbeat more often than its TTL, and the TTL must
 * exceed the longest single operation it performs without heartbeating.
 */
export const DEFAULT_LEASE_TTL_MS = 60_000;

/** Default lease-expiry sweep cadence (ms). */
const DEFAULT_LEASE_CHECK_INTERVAL_MS = 1000;

/**
 * How far past its due time a sweep must run before the pass is read as "the
 * event loop was blocked" rather than ordinary timer jitter, as a multiple of
 * {@link WorkerPoolOptions.leaseCheckIntervalMs}. At the default 1s cadence that
 * is a 5s threshold, comfortably above jitter and GC pauses.
 *
 * The threshold must also stay *below* the shortest stall that can lapse a lease,
 * or a stall expires healthy children with no grace. That coupling is real but
 * implicit: no caller overrides `leaseCheckIntervalMs` today, so the threshold is
 * always 5s, while the shortest lapsing stall is the lease-TTL floor (10s) minus
 * the holder's heartbeat cadence (ttl/3) ≈ 6.7s. The margin is ~1.7s, not a wide
 * one — raising the sweep cadence or lowering the TTL floor would silently give
 * the grace back. Re-check both together.
 */
const STALL_DELAY_FACTOR = 5;

interface WorkerPoolLeaseCoordinatorOptions<Handle> {
  readonly driver: Pick<WorkerDriver<Handle>, 'healthy' | 'onBreach' | 'reset'>;
  readonly children: Set<PooledChild<Handle>>;
  readonly idle: PooledChild<Handle>[];
  readonly logger: BrowserLogger;
  readonly now: () => number;
  readonly monotonicNow: () => number;
  readonly defaultLeaseTtlMs: number | undefined;
  readonly leaseCheckIntervalMs: number | undefined;
  readonly isDraining: () => boolean;
  readonly releaseIdle: (worker: PooledChild<Handle>) => void;
  readonly retire: (worker: PooledChild<Handle>, error: Error, force?: boolean) => void;
  readonly killWorker: (worker: PooledChild<Handle>) => void;
  readonly spawnReplacement: () => void;
}

export class WorkerPoolLeaseCoordinator<Handle> {
  private readonly driver: Pick<WorkerDriver<Handle>, 'healthy' | 'onBreach' | 'reset'>;
  private readonly children: Set<PooledChild<Handle>>;
  private readonly idle: PooledChild<Handle>[];
  private readonly logger: BrowserLogger;
  private readonly now: () => number;
  private readonly monotonicNow: () => number;
  private readonly defaultLeaseTtlMs: number;
  private readonly leaseCheckIntervalMs: number;
  private readonly isDraining: () => boolean;
  private readonly releaseIdle: (worker: PooledChild<Handle>) => void;
  private readonly retire: (worker: PooledChild<Handle>, error: Error, force?: boolean) => void;
  private readonly killWorker: (worker: PooledChild<Handle>) => void;
  private readonly spawnReplacement: () => void;
  private readonly leaseGrace: WorkerPoolLeaseGraceCounts = {
    graced: 0,
    declined: 0,
    unaffected: 0,
  };
  /** Lease-expiry sweep timer; armed only while >=1 lease is active, null otherwise. */
  private leaseTimer: ReturnType<typeof setInterval> | null = null;
  /**
   * Monotonic (`performance.now`) timestamp the next sweep is due at; null while
   * the sweep is disarmed. Compared against the pass's actual monotonic time to
   * measure how late the sweep itself ran -- see {@link sweepDelayMs}.
   */
  private expectedNextSweepAt: number | null = null;

  constructor(options: WorkerPoolLeaseCoordinatorOptions<Handle>) {
    this.driver = options.driver;
    this.children = options.children;
    this.idle = options.idle;
    this.logger = options.logger;
    this.now = options.now;
    this.monotonicNow = options.monotonicNow;
    this.defaultLeaseTtlMs = options.defaultLeaseTtlMs ?? DEFAULT_LEASE_TTL_MS;
    this.leaseCheckIntervalMs = options.leaseCheckIntervalMs ?? DEFAULT_LEASE_CHECK_INTERVAL_MS;
    this.isDraining = options.isDraining;
    this.releaseIdle = options.releaseIdle;
    this.retire = options.retire;
    this.killWorker = options.killWorker;
    this.spawnReplacement = options.spawnReplacement;
  }

  createLease(
    worker: PooledChild<Handle>,
    handle: Handle,
    opts?: WorkerPoolLeaseOptions
  ): Lease<Handle> {
    const ttlMs = opts?.ttlMs ?? this.defaultLeaseTtlMs;
    const createdAt = this.now();
    const leaseState: LeaseState = {
      createdAt,
      lastHeartbeatAt: createdAt,
      expiresAt: createdAt + ttlMs,
      ttlMs,
      heartbeatCount: 0,
      stallGraceUsed: false,
      ...(opts?.telemetryContext ? { telemetryContext: opts.telemetryContext } : {}),
    };
    worker.lease = leaseState;
    this.startSweep();
    return {
      handle,
      // Guard every method on `worker.lease === leaseState`: once the child is
      // reclaimed (expiry / crash / drain) the coordinator clears `lease`, so a
      // late heartbeat/release from the holder becomes a safe no-op.
      heartbeat: () => {
        if (worker.lease !== leaseState) return;
        const heartbeatAt = this.now();
        leaseState.lastHeartbeatAt = heartbeatAt;
        leaseState.expiresAt = heartbeatAt + ttlMs;
        leaseState.heartbeatCount++;
        // Proof the holder is alive: re-arm its stall grace. Without this a
        // long-lived session would be spared the first stall and killed by the
        // second, however healthy it had been in between.
        leaseState.stallGraceUsed = false;
      },
      release: async () => {
        if (worker.lease !== leaseState) return;
        // Clear `lease` before the awaited recycle hooks so a concurrent
        // heartbeat/release is a no-op and `retire`'s drain-defer branch (which
        // requires `worker.lease`) can't fire mid-release. The slot reads as
        // "busy but not leased" (stats().leased drops) until releaseLeased settles.
        worker.lease = null;
        await this.releaseLeased(worker);
      },
    };
  }

  countLeased(): number {
    let leased = 0;
    for (const worker of this.children) if (worker.lease) leased++;
    return leased;
  }

  graceStats(): WorkerPoolLeaseGraceCounts {
    return { ...this.leaseGrace };
  }

  stop(): void {
    if (this.leaseTimer) {
      clearInterval(this.leaseTimer);
      this.leaseTimer = null;
    }
    // Belt and braces: `startSweep` re-baselines whenever it re-arms, so this is
    // never load-bearing today. It keeps "no timer ⇒ no baseline" true, so the idle
    // gap between disarm and re-arm can never be read back as a stall.
    this.expectedNextSweepAt = null;
  }

  deferRetire(worker: PooledChild<Handle>, error: Error): boolean {
    if (this.driver.onBreach !== 'drain' || !worker.lease) return false;
    if (worker.retireOnRelease) return true; // already deferred
    worker.retireOnRelease = error;
    const idx = this.idle.indexOf(worker);
    if (idx >= 0) this.idle.splice(idx, 1);
    this.logger.warn('[WorkerPool] deferring retire of leased worker (onBreach=drain)', {
      pid: worker.child.pid,
      reason: error.message,
    });
    return true;
  }

  /**
   * Return a leased child to the pool. If a breach was deferred while the lease
   * was live (`onBreach: 'drain'`), apply that retire now. Otherwise consult the
   * driver's recycle hooks: an unhealthy child (or one whose `reset` throws) is
   * recycled (kill + respawn); a healthy one is `reset` and returned to idle.
   */
  private async releaseLeased(worker: PooledChild<Handle>): Promise<void> {
    if (worker.retired) return;
    if (worker.retireOnRelease) {
      // A breach was deferred while the lease was live; apply it now. `retire`
      // reads and clears `retireOnRelease` and classifies the kill by that reason.
      this.retire(worker, worker.retireOnRelease, true);
      return;
    }
    const handle = worker.handle;
    if (handle === undefined) {
      this.retire(worker, new WorkerPoolError('released worker has no handle'), true);
      return;
    }
    let recycleReason: string | null = null;
    try {
      if (this.driver.healthy && !(await this.driver.healthy(handle))) {
        recycleReason = 'driver reported unhealthy';
      } else if (this.driver.reset) {
        await this.driver.reset(handle);
      }
    } catch (err) {
      // A throwing health/reset hook => the child is not safe to reuse: recycle it.
      recycleReason = `recycle hook threw: ${err instanceof Error ? err.message : String(err)}`;
    }
    // The child may have crashed (or the pool drained) during the awaited hooks.
    if (worker.retired || this.isDraining()) return;
    if (recycleReason !== null) {
      this.recycle(worker, recycleReason);
      return;
    }
    this.releaseIdle(worker);
  }

  /**
   * Discard a dirty/unhealthy child after a lease and spawn a fresh one. Unlike
   * hard retire this is a planned lifecycle event, not a fault, so it does not
   * touch kill counters.
   */
  private recycle(worker: PooledChild<Handle>, reason: string): void {
    if (worker.retired) return;
    worker.retired = true;
    worker.lease = null;
    this.children.delete(worker);
    const idx = this.idle.indexOf(worker);
    if (idx >= 0) this.idle.splice(idx, 1);
    this.logger.debug('[WorkerPool] recycling worker after lease', {
      pid: worker.child.pid,
      reason,
    });
    this.killWorker(worker);
    if (this.isDraining()) return;
    this.spawnReplacement();
  }

  /**
   * Arm the lease-expiry sweep on first lease. Like the RSS monitor, the interval
   * is `.unref()`'d and self-disarms once no leases remain, so a per-call-only
   * pool never runs it.
   */
  private startSweep(): void {
    if (this.leaseTimer !== null || this.isDraining()) return;
    this.leaseTimer = setInterval(() => this.sweepLeases(), this.leaseCheckIntervalMs);
    this.leaseTimer.unref?.();
    this.expectedNextSweepAt = this.monotonicNow() + this.leaseCheckIntervalMs;
  }

  /**
   * How late this sweep ran, in ms, or null if it ran near enough on time.
   * Re-baselines the next due time either way.
   *
   * Lateness is measured on a **monotonic** clock, deliberately not the injectable
   * `now()`: the two answer different questions. `now()` is the wall clock the TTL
   * is denominated in ("has the lease elapsed?"); this one reports only whether
   * the loop was alive to run the timer ("could anyone have heartbeated?").
   * Conflating them would let a wall-clock jump masquerade as a stall.
   */
  private sweepDelayMs(): number | null {
    const monotonicNow = this.monotonicNow();
    const expected = this.expectedNextSweepAt;
    this.expectedNextSweepAt = monotonicNow + this.leaseCheckIntervalMs;
    if (expected === null) return null;
    const delayMs = monotonicNow - expected;
    return delayMs > this.leaseCheckIntervalMs * STALL_DELAY_FACTOR ? delayMs : null;
  }

  /**
   * The sweep and the holders' heartbeats share the event loop they police, so a
   * blocked loop silences both: on unblock every lease looks lapsed against a wall
   * clock that kept running. Expiring them would retire healthy children for a
   * fault they never had. Restart each lease's TTL window from the moment the loop
   * came back instead, and let the holder prove liveness.
   *
   * Bounds that keep this from becoming a licence to never expire:
   * - measured from `now()`, not added to `expiresAt` — a mid-stall lapse has
   *   already left that far in the past, so adding to it would still read expired;
   * - capped at the lease's own `ttlMs`, so one stall buys at most one window;
   * - **one window per heartbeat** ({@link LeaseState.stallGraceUsed}) — otherwise
   *   back-to-back stalls each grant a fresh window and an abandoned lease is never
   *   reclaimed at all, which is the opposite of what the TTL is for;
   * - never shortens a lease whose expiry already sits further out.
   *
   * Never grants to a child carrying a deferred breach: that retirement rests on
   * an independent reading (an RSS-cap breach the pool already took), not on the
   * liveness inference a stall invalidates. The TTL is that deferral's ceiling, and
   * memory pressure is itself a stall source — extending it would let the pool
   * shield the very child driving the pressure.
   */
  private graceLease(
    worker: PooledChild<Handle>,
    lease: LeaseState,
    delayMs: number,
    now: number
  ): WorkerPoolLeaseGraceOutcome {
    // Need before eligibility: a lease that already outlives the stall is
    // `unaffected` whether or not it *could* have been graced. Testing eligibility
    // first would report an ineligible-but-fine lease as `declined` -- which reads
    // as "about to be reclaimed" and is the signal the runbook acts on. Neither
    // check mutates, so the order is free.
    const extendedTo = Math.max(lease.expiresAt, now + Math.min(delayMs, lease.ttlMs));
    if (extendedTo === lease.expiresAt) return 'unaffected';
    if (worker.retireOnRelease || lease.stallGraceUsed) return 'declined';
    lease.expiresAt = extendedTo;
    lease.stallGraceUsed = true;
    return 'graced';
  }

  /** Apply {@link graceLease} to every live lease and report what it did. */
  private graceLeasesAfterStall(delayMs: number, now: number): void {
    let live = 0;
    const outcomes: WorkerPoolLeaseGraceCounts = { graced: 0, declined: 0, unaffected: 0 };
    // Which leases, not just how many: during a stall these are the sessions that
    // froze, and `[WorkerPool] lease expired` -- the only other line carrying their
    // identity -- is precisely what grace suppresses.
    const leases: Array<Record<string, unknown>> = [];
    // Unlike sweepLeases below, this needs no snapshot: it only moves expiry
    // dates. Keep it that way -- retiring from here would mutate `children`
    // mid-iteration.
    for (const worker of this.children) {
      const lease = worker.lease;
      if (!lease) continue;
      live++;
      const outcome = this.graceLease(worker, lease, delayMs, now);
      outcomes[outcome]++;
      // Nested, not spread: `telemetryContext` is caller-supplied `Record<string,
      // string>`, so a spread would let a stray `pid`/`outcome` key overwrite the
      // real one. Matches the shape the sibling `lease expired` line logs.
      leases.push({ pid: worker.child.pid, outcome, lease: lease.telemetryContext });
    }
    if (live === 0) return;
    this.leaseGrace.graced += outcomes.graced;
    this.leaseGrace.declined += outcomes.declined;
    this.leaseGrace.unaffected += outcomes.unaffected;
    // `declined` is the one that needs an operator: the stall bought those leases
    // nothing, so if they lapse, nothing saves them. Not a prediction that they will
    // -- a spent window can still have time left on it, and a deferred breach is
    // headed for `oom` on release rather than expiry. `unaffected` is the common,
    // benign case: the lease already outlived the stall and needed no help.
    this.logger.warn('[WorkerPool] lease sweep delayed; extending leases instead of expiring', {
      delayMs: Math.round(delayMs),
      thresholdMs: this.leaseCheckIntervalMs * STALL_DELAY_FACTOR,
      live,
      ...outcomes,
      leases,
    });
  }

  /**
   * Reclaim any lease whose TTL lapsed without a heartbeat. An expired lease has
   * no holder left to finish, so it force-retires past any `onBreach: 'drain'`
   * deferral (the TTL is the hard backstop). Self-disarms when no leases remain.
   *
   * A pass that finds the loop was blocked grants grace first, which pushes every
   * live lease past `now` -- so the expiry check below simply finds nothing to do.
   */
  private sweepLeases(): void {
    const now = this.now();
    const delayMs = this.sweepDelayMs();
    if (delayMs !== null) this.graceLeasesAfterStall(delayMs, now);
    let anyLease = false;
    // Snapshot: a retire deletes from and respawns into this.children mid-pass.
    const children = Array.from(this.children);
    for (const worker of children) {
      const lease = worker.lease;
      if (!lease) continue;
      if (lease.expiresAt <= now) {
        const error = new WorkerPoolLeaseExpiredError({
          createdAt: lease.createdAt,
          lastHeartbeatAt: lease.lastHeartbeatAt,
          expiresAt: lease.expiresAt,
          observedAt: now,
          ttlMs: lease.ttlMs,
          heartbeatCount: lease.heartbeatCount,
          ...(lease.telemetryContext ? { telemetryContext: lease.telemetryContext } : {}),
        });
        this.logger.warn('[WorkerPool] lease expired', {
          pid: worker.child.pid,
          ttlMs: lease.ttlMs,
          leaseAgeMs: now - lease.createdAt,
          heartbeatAgeMs: now - lease.lastHeartbeatAt,
          heartbeatCount: lease.heartbeatCount,
          overdueMs: now - lease.expiresAt,
          lease: lease.telemetryContext,
        });
        this.retire(worker, error, true);
      } else {
        anyLease = true;
      }
    }
    if (!anyLease) this.stop();
  }
}
